import React, { useEffect, useRef } from 'react';
import { instituteInfo, images } from '../data/mock';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Phone, Star, BookOpen, GraduationCap, Award } from 'lucide-react';

export const Hero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      heroRef.current.style.setProperty('--mouse-x', `${x}px`);
      heroRef.current.style.setProperty('--mouse-y', `${y}px`);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section id="home" ref={heroRef} className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url(${images.classroom})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(2px)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900/90" />
      </div>

      {/* Animated shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="hero-float-shape absolute top-20 left-[10%] w-72 h-72 bg-teal-500/8 rounded-full blur-3xl" style={{ animationDelay: '0s' }} />
        <div className="hero-float-shape absolute bottom-20 right-[10%] w-96 h-96 bg-amber-500/6 rounded-full blur-3xl" style={{ animationDelay: '2s' }} />
        <div className="hero-float-shape absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-400/5 rounded-full blur-3xl" style={{ animationDelay: '4s' }} />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '48px 48px' }} />
      </div>

      {/* Floating icons */}
      <div className="absolute inset-0 pointer-events-none hidden lg:block">
        <div className="hero-float-icon absolute top-32 left-[15%]" style={{ animationDelay: '0s' }}>
          <div className="w-14 h-14 rounded-2xl bg-teal-500/10 backdrop-blur-sm border border-teal-400/20 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-teal-300" />
          </div>
        </div>
        <div className="hero-float-icon absolute top-48 right-[18%]" style={{ animationDelay: '1.5s' }}>
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 backdrop-blur-sm border border-amber-400/20 flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-amber-300" />
          </div>
        </div>
        <div className="hero-float-icon absolute bottom-40 left-[20%]" style={{ animationDelay: '3s' }}>
          <div className="w-14 h-14 rounded-2xl bg-rose-500/10 backdrop-blur-sm border border-rose-400/20 flex items-center justify-center">
            <Award className="w-6 h-6 text-rose-300" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-32 lg:py-40">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="hero-fade-up mb-8">
            <Badge className="bg-teal-500/10 text-teal-300 border-teal-400/20 backdrop-blur-sm px-4 py-2 text-sm font-medium hover:bg-teal-500/15 transition-colors cursor-default">
              <Star className="w-3.5 h-3.5 mr-2 fill-amber-400 text-amber-400" />
              Rated {instituteInfo.googleRating}/5 on Google
            </Badge>
          </div>

          {/* Heading */}
          <h1 className="hero-fade-up text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.08] tracking-tight mb-6" style={{ animationDelay: '0.15s' }}>
            Where Learning
            <br />
            <span className="relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-teal-200 to-amber-300">
                Meets Excellence
              </span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="hero-fade-up text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl mb-4" style={{ animationDelay: '0.3s' }}>
            {instituteInfo.description}
          </p>

          {/* Boards badges */}
          <div className="hero-fade-up flex flex-wrap gap-3 mb-10" style={{ animationDelay: '0.4s' }}>
            {['CBSE', 'ICSE', 'WBBSE'].map((board) => (
              <span key={board} className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-white/5 text-white/80 border border-white/10 backdrop-blur-sm">
                {board}
              </span>
            ))}
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-white/5 text-white/80 border border-white/10 backdrop-blur-sm">
              KG - Class X
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="hero-fade-up flex flex-wrap gap-4" style={{ animationDelay: '0.5s' }}>
            <Button
              onClick={scrollToContact}
              className="h-14 px-8 rounded-full bg-teal-500 hover:bg-teal-400 text-white font-semibold text-base shadow-xl shadow-teal-500/20 hover:shadow-teal-400/30 transition-all duration-300 hover:-translate-y-0.5 group"
            >
              Enroll Now
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            <a href={`tel:${instituteInfo.phone}`}>
              <Button
                variant="outline"
                className="h-14 px-8 rounded-full border-white/20 text-white hover:bg-white/10 font-semibold text-base backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5"
              >
                <Phone className="w-5 h-5 mr-2" />
                {instituteInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};
